/**
 * Source References Modal - Enterprise ERP Job-to-Invoice Linking
 * 
 * NetSuite-inspired workflow for importing billable work items into invoices.
 * Supports: Jobs, Tickets, Tasks, Projects, Maintenance, Time Entries
 * 
 * @version 1.0.0
 * @date December 2025
 */

'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X, Search, Filter, Check, Clock, DollarSign, Package,
  Briefcase, Wrench, ClipboardList, AlertCircle, ChevronDown,
  ChevronRight, Calendar, User, Building, FileText, ArrowRight,
  Layers, CheckSquare, Square, Timer, Plus, Minus, RefreshCw,
  Target, Ticket, Settings, HardDrive, Truck
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import toast from 'react-hot-toast';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// TYPES
// ============================================================================

export type BillableItemType = 
  | 'job' 
  | 'ticket' 
  | 'task' 
  | 'project' 
  | 'maintenance'
  | 'inspection'
  | 'delivery'
  | 'milestone';

export interface TimeEntry {
  id: string;
  user_id: number;
  user_name: string;
  started_at: string;
  ended_at: string | null;
  duration_minutes: number;
  duration_hours: number;
  is_billable: boolean;
  hourly_rate: number;
  total_cost: number;
  billed: boolean;
  description: string;
  activity_type: string;
}

export interface BOMItem {
  id: number;
  item_name: string;
  item_code?: string;
  item_id?: number;
  description?: string;
  quantity: number;
  unit: string;
  unit_cost: number;
  total_cost: number;
  tax_rate?: number;
  tax_amount?: number;
}

export interface ExpenseItem {
  id: string;
  item_number: string;
  item_type: string;
  title: string;
  status: string;
  billed: boolean;
  amount: number;
  supplier_name?: string;
  expense_category?: string;
  receipt_url?: string;
  created_at: string;
}

export interface BillableWorkItem {
  id: string;
  item_number: string;
  item_type: BillableItemType;
  title: string;
  description?: string;
  status: string;
  contact_id?: number;
  contact_name?: string;
  assigned_to?: number;
  assigned_to_name?: string;
  created_at: string;
  completed_at?: string;
  due_date?: string;
  
  // Billing info
  billable: boolean;
  billed: boolean;
  invoice_id?: string;
  estimated_hours?: number;
  actual_hours?: number;
  hourly_rate?: number;
  estimated_cost?: number;
  actual_cost?: number;
  
  // Related data
  time_entries?: TimeEntry[];
  bom_items?: BOMItem[];
  expenses?: ExpenseItem[];
  unbilled_time_hours?: number;
  unbilled_time_amount?: number;
  unbilled_materials_amount?: number;
  unbilled_expenses_amount?: number;
  
  // Selection state (UI only)
  selected?: boolean;
  expanded?: boolean;
}

export interface SelectedBillableItem {
  workItem: BillableWorkItem;
  includeTimeEntries: boolean;
  includeMaterials: boolean;
  includeExpenses: boolean;
  selectedTimeEntryIds: string[];
  selectedBOMItemIds: number[];
  selectedExpenseIds: string[];
  customDescription?: string;
  customRate?: number;
}

export interface SourceReferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactId?: number;
  contactName?: string;
  onImport: (items: SelectedBillableItem[]) => void;
  existingLinkedItemIds?: string[];
}

// ============================================================================
// ITEM TYPE CONFIG
// ============================================================================

const ITEM_TYPE_CONFIG: Record<BillableItemType, {
  label: string;
  pluralLabel: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
}> = {
  job: { 
    label: 'Job', 
    pluralLabel: 'Jobs', 
    icon: Briefcase, 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-100' 
  },
  ticket: { 
    label: 'Ticket', 
    pluralLabel: 'Tickets', 
    icon: Ticket, 
    color: 'text-purple-600', 
    bgColor: 'bg-purple-100' 
  },
  task: { 
    label: 'Task', 
    pluralLabel: 'Tasks', 
    icon: ClipboardList, 
    color: 'text-green-600', 
    bgColor: 'bg-green-100' 
  },
  project: { 
    label: 'Project', 
    pluralLabel: 'Projects', 
    icon: Layers, 
    color: 'text-indigo-600', 
    bgColor: 'bg-indigo-100' 
  },
  maintenance: { 
    label: 'Maintenance', 
    pluralLabel: 'Maintenance', 
    icon: Wrench, 
    color: 'text-orange-600', 
    bgColor: 'bg-orange-100' 
  },
  inspection: { 
    label: 'Inspection', 
    pluralLabel: 'Inspections', 
    icon: ClipboardList, 
    color: 'text-teal-600', 
    bgColor: 'bg-teal-100' 
  },
  delivery: { 
    label: 'Delivery', 
    pluralLabel: 'Deliveries', 
    icon: Truck, 
    color: 'text-cyan-600', 
    bgColor: 'bg-cyan-100' 
  },
  milestone: { 
    label: 'Milestone', 
    pluralLabel: 'Milestones', 
    icon: Target, 
    color: 'text-pink-600', 
    bgColor: 'bg-pink-100' 
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function SourceReferencesModal({
  isOpen,
  onClose,
  contactId,
  contactName,
  onImport,
  existingLinkedItemIds = []
}: SourceReferencesModalProps) {
  const { currentTheme, clientBranding } = useTheme();
  const isDarkMode = currentTheme.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || '#3b82f6';
  
  // State
  const [loading, setLoading] = useState(false);
  const [billableItems, setBillableItems] = useState<BillableWorkItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Map<string, SelectedBillableItem>>(new Map());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<BillableItemType[]>(['job', 'ticket', 'task', 'delivery']);
  const [showBilledItems, setShowBilledItems] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'ready_for_billing' | 'all'>('ready_for_billing'); // ready_for_billing = pending_approval or completed
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'created_at' | 'completed_at' | 'actual_hours'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Refs to prevent infinite loops
  const existingLinkedIdsRef = React.useRef(existingLinkedItemIds);
  const hasLoadedRef = React.useRef(false);
  const searchDebounceRef = React.useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Update ref when prop changes
  React.useEffect(() => {
    existingLinkedIdsRef.current = existingLinkedItemIds;
  }, [existingLinkedItemIds]);
  
  // Debounce search input
  React.useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  // Load billable work items
  const loadBillableItems = useCallback(async () => {
    setLoading(true);
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('No auth token');

      const params = new URLSearchParams({
        item_types: JSON.stringify(selectedTypes),
        billable: 'true',
        include_time_entries: 'true',
        include_bom_items: 'true',
        include_expenses: 'true',
        sort_by: sortField,
        sort_order: sortOrder,
        limit: '100'
      });

      if (contactId) {
        params.set('contact_id', String(contactId));
      }

      if (!showBilledItems) {
        params.set('billed', 'false');
      }

      // Filter by status - ready_for_billing shows only admin-approved or pending_approval jobs
      if (statusFilter === 'ready_for_billing') {
        params.set('status', 'pending_approval,completed');
      }

      if (debouncedSearch) {
        params.set('search', debouncedSearch);
      }

      const response = await fetch(`/api/v1/work-items/billable?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success && result.data) {
        // Filter out already linked items using ref
        const filtered = result.data.filter((item: BillableWorkItem) => 
          !existingLinkedIdsRef.current.includes(item.id)
        );
        setBillableItems(filtered);
      }
    } catch (error: any) {
      console.error('Error loading billable items:', error);
      toast.error('Failed to load billable items');
    } finally {
      setLoading(false);
    }
  }, [contactId, selectedTypes, showBilledItems, statusFilter, debouncedSearch, sortField, sortOrder]);

  // Load on open and when filters change
  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      if (!hasLoadedRef.current) {
        hasLoadedRef.current = true;
      }
      loadBillableItems();
    } else {
      // Reset when modal closes
      hasLoadedRef.current = false;
    }
  }, [isOpen, loadBillableItems]);

  // Toggle item selection
  const toggleItemSelection = (item: BillableWorkItem) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev);
      if (newMap.has(item.id)) {
        newMap.delete(item.id);
      } else {
        newMap.set(item.id, {
          workItem: item,
          includeTimeEntries: true,
          includeMaterials: true,
          includeExpenses: true,
          selectedTimeEntryIds: item.time_entries?.filter(t => !t.billed).map(t => t.id) || [],
          selectedBOMItemIds: item.bom_items?.map(b => b.id) || [],
          selectedExpenseIds: item.expenses?.filter(e => !e.billed).map(e => e.id) || [],
        });
      }
      return newMap;
    });
  };

  // Toggle time entry selection
  const toggleTimeEntry = (itemId: string, entryId: string) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev);
      const selection = newMap.get(itemId);
      if (selection) {
        const ids = selection.selectedTimeEntryIds;
        if (ids.includes(entryId)) {
          selection.selectedTimeEntryIds = ids.filter(id => id !== entryId);
        } else {
          selection.selectedTimeEntryIds = [...ids, entryId];
        }
        newMap.set(itemId, { ...selection });
      }
      return newMap;
    });
  };

  // Toggle BOM item selection
  const toggleBOMItem = (itemId: string, bomId: number) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev);
      const selection = newMap.get(itemId);
      if (selection) {
        const ids = selection.selectedBOMItemIds;
        if (ids.includes(bomId)) {
          selection.selectedBOMItemIds = ids.filter(id => id !== bomId);
        } else {
          selection.selectedBOMItemIds = [...ids, bomId];
        }
        newMap.set(itemId, { ...selection });
      }
      return newMap;
    });
  };

  // Toggle expense selection
  const toggleExpense = (itemId: string, expenseId: string) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev);
      const selection = newMap.get(itemId);
      if (selection) {
        const ids = selection.selectedExpenseIds;
        if (ids.includes(expenseId)) {
          selection.selectedExpenseIds = ids.filter(id => id !== expenseId);
        } else {
          selection.selectedExpenseIds = [...ids, expenseId];
        }
        newMap.set(itemId, { ...selection });
      }
      return newMap;
    });
  };

  // Calculate totals
  const selectedTotals = useMemo(() => {
    let timeHours = 0;
    let timeAmount = 0;
    let materialsAmount = 0;
    let expensesAmount = 0;
    let itemCount = 0;

    selectedItems.forEach((selection) => {
      itemCount++;
      
      // Time entries
      if (selection.includeTimeEntries) {
        selection.workItem.time_entries?.forEach(entry => {
          if (selection.selectedTimeEntryIds.includes(entry.id)) {
            timeHours += entry.duration_hours;
            timeAmount += entry.total_cost;
          }
        });
      }
      
      // Materials
      if (selection.includeMaterials) {
        selection.workItem.bom_items?.forEach(bom => {
          if (selection.selectedBOMItemIds.includes(bom.id)) {
            materialsAmount += bom.total_cost;
          }
        });
      }

      // Expenses
      if (selection.includeExpenses) {
        selection.workItem.expenses?.forEach(exp => {
          if (selection.selectedExpenseIds.includes(exp.id)) {
            expensesAmount += exp.amount;
          }
        });
      }
    });

    return { itemCount, timeHours, timeAmount, materialsAmount, expensesAmount, totalAmount: timeAmount + materialsAmount + expensesAmount };
  }, [selectedItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    return billableItems.filter(item => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        if (!item.title.toLowerCase().includes(search) &&
            !item.item_number.toLowerCase().includes(search) &&
            !(item.contact_name?.toLowerCase().includes(search))) {
          return false;
        }
      }
      return true;
    });
  }, [billableItems, searchTerm]);

  // Handle import
  const handleImport = () => {
    if (selectedItems.size === 0) {
      toast.error('Please select at least one item to import');
      return;
    }
    onImport(Array.from(selectedItems.values()));
    onClose();
  };

  // Format currency
  const formatCurrency = (val: number) => val.toLocaleString('en-ZA', { minimumFractionDigits: 2 });

  // Format duration
  const formatDuration = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[140] flex items-start justify-center overflow-y-auto bg-black/60 p-4">
      <div className={`relative w-full max-w-5xl my-8 rounded-xl shadow-2xl ${
        isDarkMode ? 'bg-gray-900' : 'bg-white'
      }`}>
        
        {/* Header */}
        <div 
          className="px-6 py-5 rounded-t-xl text-white"
          style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-white/20 backdrop-blur rounded-lg">
                <FileText className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold">Import Billable Work Items</h2>
                <p className="text-sm text-white/80 mt-0.5">
                  {contactName ? `For ${contactName}` : 'Select jobs, tickets, or tasks to bill'}
                </p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[250px]">
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                isDarkMode ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <input
                type="text"
                placeholder="Search by number, title, or contact..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border text-sm ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                }`}
              />
            </div>

            {/* Item Type Toggles */}
            <div className="flex items-center gap-2">
              {Object.entries(ITEM_TYPE_CONFIG).slice(0, 5).map(([type, config]) => {
                const Icon = config.icon;
                const isSelected = selectedTypes.includes(type as BillableItemType);
                return (
                  <button
                    key={type}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedTypes(prev => prev.filter(t => t !== type));
                      } else {
                        setSelectedTypes(prev => [...prev, type as BillableItemType]);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      isSelected
                        ? `${config.bgColor} ${config.color}`
                        : isDarkMode 
                          ? 'bg-gray-700 text-gray-400 hover:bg-gray-600' 
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <Icon size={14} />
                    {config.pluralLabel}
                  </button>
                );
              })}
            </div>

            {/* Show Billed Toggle */}
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <input
                type="checkbox"
                checked={showBilledItems}
                onChange={(e) => setShowBilledItems(e.target.checked)}
                className="rounded"
              />
              Show already billed
            </label>

            {/* Status Filter Toggle */}
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <input
                type="checkbox"
                checked={statusFilter === 'all'}
                onChange={(e) => setStatusFilter(e.target.checked ? 'all' : 'ready_for_billing')}
                className="rounded"
              />
              Show all statuses
            </label>

            {/* Refresh */}
            <button
              onClick={loadBillableItems}
              disabled={loading}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
              }`}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`max-h-[50vh] overflow-y-auto ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className={`text-center py-16 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <Briefcase size={48} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">No billable items found</p>
              <p className="text-sm">
                {contactId 
                  ? 'No unbilled work items for this contact'
                  : 'Select a contact or adjust filters to see billable items'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredItems.map((item) => {
                const config = ITEM_TYPE_CONFIG[item.item_type] || ITEM_TYPE_CONFIG.job;
                const Icon = config.icon;
                const isSelected = selectedItems.has(item.id);
                const isExpanded = expandedItemId === item.id;
                const selection = selectedItems.get(item.id);
                
                const unbilledTimeEntries = item.time_entries?.filter(t => !t.billed) || [];
                const totalUnbilledTime = unbilledTimeEntries.reduce((sum, t) => sum + t.duration_hours, 0);
                const totalUnbilledTimeAmount = unbilledTimeEntries.reduce((sum, t) => sum + t.total_cost, 0);
                const totalMaterialsAmount = item.bom_items?.reduce((sum, b) => sum + b.total_cost, 0) || 0;
                const unbilledExpenses = item.expenses?.filter(e => !e.billed) || [];
                const totalUnbilledExpensesAmount = unbilledExpenses.reduce((sum, e) => sum + e.amount, 0);

                return (
                  <div 
                    key={item.id}
                    className={`${
                      isSelected 
                        ? isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50' 
                        : ''
                    }`}
                  >
                    {/* Main Row */}
                    <div className="flex items-center gap-4 px-6 py-4">
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleItemSelection(item)}
                        className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-500 text-white'
                            : isDarkMode
                              ? 'border-gray-600 hover:border-gray-500'
                              : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {isSelected && <Check size={12} />}
                      </button>

                      {/* Expand Button */}
                      <button
                        onClick={() => setExpandedItemId(isExpanded ? null : item.id)}
                        className={`flex-shrink-0 p-1 rounded transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                        }`}
                      >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>

                      {/* Type Icon */}
                      <div className={`flex-shrink-0 p-2 rounded-lg ${config.bgColor}`}>
                        <Icon size={16} className={config.color} />
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {item.item_number}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            item.status === 'completed' 
                              ? 'bg-green-100 text-green-700' 
                              : item.status === 'pending_approval'
                                ? 'bg-amber-100 text-amber-700'
                                : item.status === 'in_progress'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.status === 'pending_approval' ? 'Awaiting Approval' : item.status.replace(/_/g, ' ')}
                          </span>
                          {item.billed && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-700">
                              Previously Billed
                            </span>
                          )}
                        </div>
                        <h4 className={`font-medium mt-1 truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.title}
                        </h4>
                        <div className={`flex items-center gap-4 mt-1 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {item.contact_name && (
                            <span className="flex items-center gap-1">
                              <Building size={12} />
                              {item.contact_name}
                            </span>
                          )}
                          {item.assigned_to_name && (
                            <span className="flex items-center gap-1">
                              <User size={12} />
                              {item.assigned_to_name}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {/* Summary Stats */}
                      <div className="flex items-center gap-6 flex-shrink-0">
                        {/* Time */}
                        {unbilledTimeEntries.length > 0 && (
                          <div className="text-right">
                            <div className={`flex items-center gap-1 text-sm font-medium ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <Clock size={14} className="text-purple-500" />
                              {formatDuration(totalUnbilledTime)}
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              R {formatCurrency(totalUnbilledTimeAmount)}
                            </div>
                          </div>
                        )}
                        
                        {/* Materials */}
                        {item.bom_items && item.bom_items.length > 0 && (
                          <div className="text-right">
                            <div className={`flex items-center gap-1 text-sm font-medium ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <Package size={14} className="text-orange-500" />
                              {item.bom_items.length} items
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              R {formatCurrency(totalMaterialsAmount)}
                            </div>
                          </div>
                        )}

                        {/* Expenses */}
                        {unbilledExpenses.length > 0 && (
                          <div className="text-right">
                            <div className={`flex items-center gap-1 text-sm font-medium ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <DollarSign size={14} className="text-green-500" />
                              {unbilledExpenses.length} expense{unbilledExpenses.length !== 1 ? 's' : ''}
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              R {formatCurrency(totalUnbilledExpensesAmount)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className={`px-6 pb-4 ml-16 ${isDarkMode ? 'bg-gray-800/30' : 'bg-gray-50/50'}`}>
                        {/* Time Entries */}
                        {unbilledTimeEntries.length > 0 && (
                          <div className="mb-4">
                            <h5 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <Clock size={14} className="text-purple-500" />
                              Time Entries ({unbilledTimeEntries.length})
                            </h5>
                            <div className="space-y-2">
                              {unbilledTimeEntries.map(entry => {
                                const isEntrySelected = selection?.selectedTimeEntryIds.includes(entry.id);
                                return (
                                  <div 
                                    key={entry.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg ${
                                      isDarkMode ? 'bg-gray-700/50' : 'bg-white'
                                    }`}
                                  >
                                    <button
                                      onClick={() => isSelected && toggleTimeEntry(item.id, entry.id)}
                                      disabled={!isSelected}
                                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                                        !isSelected 
                                          ? 'opacity-50 cursor-not-allowed' 
                                          : isEntrySelected
                                            ? 'border-purple-500 bg-purple-500 text-white'
                                            : 'border-gray-400'
                                      }`}
                                    >
                                      {isEntrySelected && <Check size={10} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {entry.description || 'Time entry'}
                                      </div>
                                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {entry.user_name} • {new Date(entry.started_at).toLocaleDateString()}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {formatDuration(entry.duration_hours)}
                                      </div>
                                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        R {formatCurrency(entry.total_cost)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Materials / BOM Items */}
                        {item.bom_items && item.bom_items.length > 0 && (
                          <div>
                            <h5 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <Package size={14} className="text-orange-500" />
                              Materials ({item.bom_items.length})
                            </h5>
                            <div className="space-y-2">
                              {item.bom_items.map(bom => {
                                const isBOMSelected = selection?.selectedBOMItemIds.includes(bom.id);
                                return (
                                  <div 
                                    key={bom.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg ${
                                      isDarkMode ? 'bg-gray-700/50' : 'bg-white'
                                    }`}
                                  >
                                    <button
                                      onClick={() => isSelected && toggleBOMItem(item.id, bom.id)}
                                      disabled={!isSelected}
                                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                                        !isSelected 
                                          ? 'opacity-50 cursor-not-allowed' 
                                          : isBOMSelected
                                            ? 'border-orange-500 bg-orange-500 text-white'
                                            : 'border-gray-400'
                                      }`}
                                    >
                                      {isBOMSelected && <Check size={10} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {bom.item_name}
                                      </div>
                                      {bom.item_code && (
                                        <div className={`text-xs font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                          {bom.item_code}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {bom.quantity} {bom.unit} × R {formatCurrency(bom.unit_cost)}
                                      </div>
                                      <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        R {formatCurrency(bom.total_cost)}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Expenses */}
                        {unbilledExpenses.length > 0 && (
                          <div className="mt-4">
                            <h5 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${
                              isDarkMode ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              <DollarSign size={14} className="text-green-500" />
                              Expenses ({unbilledExpenses.length})
                            </h5>
                            <div className="space-y-2">
                              {unbilledExpenses.map(exp => {
                                const isExpSelected = selection?.selectedExpenseIds.includes(exp.id);
                                const categoryLabel: Record<string, string> = {
                                  fuel: 'Fuel', toll: 'Toll', parking: 'Parking',
                                  purchase: 'Purchase', maintenance: 'Maintenance',
                                  breakdown: 'Breakdown', vehicle_expense: 'Vehicle',
                                };
                                return (
                                  <div
                                    key={exp.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg ${
                                      isDarkMode ? 'bg-gray-700/50' : 'bg-white'
                                    }`}
                                  >
                                    <button
                                      onClick={() => isSelected && toggleExpense(item.id, exp.id)}
                                      disabled={!isSelected}
                                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                                        !isSelected
                                          ? 'opacity-50 cursor-not-allowed'
                                          : isExpSelected
                                            ? 'border-green-500 bg-green-500 text-white'
                                            : 'border-gray-400'
                                      }`}
                                    >
                                      {isExpSelected && <Check size={10} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {exp.title || categoryLabel[exp.expense_category || ''] || 'Expense'}
                                      </div>
                                      <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {categoryLabel[exp.expense_category || ''] || exp.item_type}
                                        {exp.supplier_name ? ` • ${exp.supplier_name}` : ''}
                                        {' • '}
                                        {new Date(exp.created_at).toLocaleDateString()}
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                        R {formatCurrency(exp.amount)}
                                      </div>
                                      <div className={`text-xs font-mono ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {exp.item_number}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with Totals */}
        <div className={`px-6 py-4 border-t rounded-b-xl ${
          isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center justify-between">
            {/* Selection Summary */}
            <div className="flex items-center gap-6">
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="font-medium">{selectedTotals.itemCount}</span> items selected
              </div>
              
              {selectedTotals.timeHours > 0 && (
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-purple-500" />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatDuration(selectedTotals.timeHours)}
                  </span>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    R {formatCurrency(selectedTotals.timeAmount)}
                  </span>
                </div>
              )}
              
              {selectedTotals.materialsAmount > 0 && (
                <div className="flex items-center gap-2">
                  <Package size={14} className="text-orange-500" />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    R {formatCurrency(selectedTotals.materialsAmount)}
                  </span>
                </div>
              )}

              {selectedTotals.expensesAmount > 0 && (
                <div className="flex items-center gap-2">
                  <DollarSign size={14} className="text-green-500" />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                    R {formatCurrency(selectedTotals.expensesAmount)}
                  </span>
                </div>
              )}
              
              {selectedTotals.totalAmount > 0 && (
                <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Total: R {formatCurrency(selectedTotals.totalAmount)}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={selectedItems.size === 0}
                className={`px-6 py-2 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2 ${
                  selectedItems.size === 0 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:opacity-90'
                }`}
                style={{ backgroundColor: primaryColor }}
              >
                <ArrowRight size={16} />
                Import {selectedItems.size} Item{selectedItems.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
